import { ArrowLeftOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import type { BaseOptionType } from 'antd/es/select';
import { useEffect } from 'react';
import styled from 'styled-components';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import useTyping from '@/hooks/pure/useTyping';
import type { LANGUAGE } from '@/types/commonConst';

type AILanguageSettingsProps = {
  langOptions: BaseOptionType[];
  language: LANGUAGE | undefined;
  setLanguage: (value: LANGUAGE | undefined) => void;
  undo: () => void;
  onComplete: () => void;
};

const SAMPLE_AI_SUMMARY_TEXT = {
  en: 'Syncly AI provides a summary based on all the data points under the topic, so you don’t have to spend a lot of time reading through each customer feedback one by one. Based on trending common theme, #keywords will be generated to identify the most frequent topics, and to provide more context, the most relevant customer quotes will be captured under each keyword along with the customer and account each quote is mentioned by.',
  ko: 'Syncly AI는 주제 아래의 모든 데이터 포인트를 기반으로 요약을 제공하므로 고객 피드백을 일일이 읽느라 많은 시간을 할애할 필요가 없습니다. 유행하는 공통 주제를 기반으로 #키워드가 생성되어 가장 빈번한 주제를 식별하고, 더 많은 컨텍스트를 제공하기 위해 각 키워드 아래에 가장 관련성이 높은 고객 의견과 각 의견이 언급된 고객 및 계정이 캡처됩니다.',
};
const ANIMATION_DELAY = 5;

const AILanguageSettings = (props: AILanguageSettingsProps) => {
  const color = useColor();
  const sampleText =
    props.language === 'ko'
      ? SAMPLE_AI_SUMMARY_TEXT.ko
      : SAMPLE_AI_SUMMARY_TEXT.en;

  const { displayedText, reset } = useTyping(sampleText, ANIMATION_DELAY);

  useEffect(() => {
    // Reset when language changes
    reset();
  }, [props.language, reset]);

  useEffect(() => {
    // Reset animation every 10 seconds
    const interval = setInterval(() => {
      reset();
    }, 10000);

    return () => clearInterval(interval);
  }, [reset]);

  return (
    <>
      <Select
        defaultValue={props.language}
        onChange={(val) => props.setLanguage(val)}
        options={props.langOptions}
        style={{
          height: '40px',
          width: '220px',
        }}
      />

      <Space h={24} />

      <AISummaryBox>
        <Flex a="center" g="2px">
          <AIIcon className="text-xl" />
          <Typo
            type="body1"
            weight="b"
            style={{
              lineHeight: '24px',
            }}
          >
            AI summary
          </Typo>
        </Flex>

        <Space h={8} />

        <Typo
          type="body1"
          style={{
            lineHeight: '24px',
          }}
        >
          {displayedText}
        </Typo>
      </AISummaryBox>

      <Space h={64} />

      <Flex a="center" j="space-between">
        <IconButton
          icon={<ArrowLeftOutlined />}
          sizeType="s"
          outlined
          style={{
            width: '40px',
            height: '40px',
            fontSize: '18px',
            color: color.primary100,
          }}
          onClick={props.undo}
        />

        <Button
          sizeType="l"
          style={{
            width: '200px',
          }}
          onClick={props.onComplete}
        >
          Next
        </Button>
      </Flex>
    </>
  );
};

const AISummaryBox = styled(VFlex)`
  background-color: rgba(0, 204, 118, 0.04);
  padding: 16px;
  border-radius: 6px;
  color: ${(props) => props.theme.colors.green500};
  height: 208px;
  ul {
    padding-left: 24px;
  }
`;

export default AILanguageSettings;
